import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpHelperService } from "@services/http-helper.service";
import { map, Observable, tap } from "rxjs";
import { environment } from "src/environments/environment";

interface RedirectResponse {
  redirectUrl?: string;
  [key: string]: unknown;
}

interface AlphaCcentricRedirectPayload {
  userId: string;
  opportunityId?: string;
  agentCode?: string;
}

interface CrossSellIssueRedirectPayload {
  action?: string;
  productType?: string;
  productCode?: string;
  agentCode?: string;
  idType?: string;
  idValue?: string;
  clientType?: string;
  userId?: string;
  documentNo?: string;
}

// Centralizes external navigation between SAT, Alpha, Alpha SAT, and SAT Motor.
@Injectable({ providedIn: "root" })
export class CrossAppNavigationService {
  private readonly alphaBaseUrl = environment.alpha_url.replace(/\/$/, "");
  private readonly alphaSatBaseUrl = environment.alpha_sat_url.replace(
    /\/$/,
    "",
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpHelperService: HttpHelperService,
  ) {}

  public backToAlpha(userId: string, autoRedirect = true): Observable<string> {
    return this.resolveRedirect(
      `/session/logout?switch&userId=${encodeURIComponent(userId)}`,
      autoRedirect,
      "backToAlpha",
    );
  }

  public backToSat(userId: string, autoRedirect = true): Observable<string> {
    return this.resolveRedirect(
      `/session/alpha-sat/SAT_SYS_URL?switch&userId=${encodeURIComponent(
        userId,
      )}`,
      autoRedirect,
      "backToSat",
    );
  }

  public signOut(userId: string, autoRedirect = true): Observable<string> {
    // After a successful logout API call, redirect based on loginSource:
    // - "sat"  → user originally logged in from SAT, send back to SAT login page (window.location.origin)
    // - other  → user originally logged in from Alpha-SAT, send back to Alpha-SAT login
    // loginSource is set at login time in login.component.ts and persists across module switches
    // via localStorage, so it correctly reflects the original entry point regardless of how many
    // times the user switched between SAT and Alpha modules during their session.
    return this.resolveRedirect(
      `/session/logout?userId=${encodeURIComponent(userId)}`,
      false,
      "signOut",
    ).pipe(
      tap(() => {
        if (!autoRedirect) {
          return;
        }
        this.redirectAfterSignOut();
      }),
    );
  }

  public redirectToAlphaSat(
    code: string,
    userId: string,
    autoRedirect = true,
  ): Observable<string> {
    return this.resolveRedirect(
      `/session/alpha-sat/${encodeURIComponent(
        code,
      )}?switch&userId=${encodeURIComponent(userId)}`,
      autoRedirect,
      "redirectToAlphaSat",
    );
  }

  public redirectToAlphaPath(
    path = "",
    target: "_self" | "_blank" = "_self",
  ): void {
    this.redirect(this.buildAlphaUrl(path), target);
  }

  public redirectToAlphaSatPath(
    path = "",
    target: "_self" | "_blank" = "_self",
  ): void {
    this.redirect(this.buildAlphaSatUrl(path), target);
  }

  public redirectAfterSignOut(loginSource?: string | null): void {
    const resolvedLoginSource =
      loginSource ?? localStorage.getItem("loginSource");
    localStorage.removeItem("loginSource");

    if (resolvedLoginSource === "sat") {
      this.redirect(globalThis.location.origin);
      return;
    }

    this.redirectToAlphaSatPath("/auth/login");
  }

  public resolveSatSwitchSite(
    moduleKey: string,
    autoRedirect = true,
  ): Observable<string> {
    const sessionBaseUrl = this.httpHelperService.resolveSessionBaseUrl();

    // Old SAT FE switch endpoint kept here for quick fallback/reference:
    // return this.resolveRedirect(
    //   `/session/${encodeURIComponent(moduleKey)}`,
    //   autoRedirect,
    //   "resolveSatSwitchSite",
    // );

    return this.resolveAbsoluteRedirect(
      `${sessionBaseUrl}/session/sat-motor/${encodeURIComponent(moduleKey)}`,
      autoRedirect,
      "resolveSatSwitchSite",
    );
  }

  public resolveMenuActionUrl(
    menuUrl: string,
    autoRedirect = true,
  ): Observable<string> {
    const normalized = this.normalizeMenuUrl(menuUrl);
    return this.http.get<RedirectResponse>(normalized).pipe(
      map((res) => this.requireRedirectUrl(res, "resolveMenuActionUrl")),
      tap((url) => {
        if (autoRedirect) {
          this.redirect(url);
        }
      }),
    );
  }

  public redirectToAlphaCCentric(
    payload: AlphaCcentricRedirectPayload,
    autoRedirect = true,
  ): Observable<string> {
    const params = new URLSearchParams();
    params.set("userId", payload.userId);

    if (payload.opportunityId) {
      params.set("opportunityId", payload.opportunityId);
    }

    if (payload.agentCode) {
      params.set("agentCode", payload.agentCode);
    }

    return this.resolveRedirect(
      `/session/logout?switchOcc&${params.toString()}`,
      autoRedirect,
      "redirectToAlphaCCentric",
    );
  }

  public redirectCrossSellIssueToAlpha(
    payload: CrossSellIssueRedirectPayload,
    autoRedirect = true,
  ): Observable<string> {
    const params = new URLSearchParams();

    if (payload.action) {
      params.set("action", payload.action);
    }

    if (payload.documentNo) {
      params.set("documentNo", payload.documentNo);
    }

    if (payload.productType) {
      params.set("productType", payload.productType);
    }

    if (payload.productCode) {
      params.set("productCode", payload.productCode);
    }

    if (payload.agentCode) {
      params.set("agentCode", payload.agentCode);
    }

    if (payload.idType) {
      params.set("idType", payload.idType);
    }

    if (payload.idValue) {
      params.set("idValue", payload.idValue);
    }

    if (payload.clientType) {
      params.set("clientType", payload.clientType);
    }

    if (payload.userId) {
      params.set("userId", payload.userId);
    }

    const queryString = params.toString();
    const path = queryString
      ? `/session/logout?switch&${queryString}`
      : "/session/logout?switch";

    return this.resolveRedirect(
      path,
      autoRedirect,
      "redirectCrossSellIssueToAlpha",
    );
  }

  public redirect(url: string, target: "_self" | "_blank" = "_self"): void {
    if (target === "_blank") {
      globalThis.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    this.navigateInSameTab(url);
  }

  private navigateInSameTab(url: string): void {
    globalThis.location.assign(url);
  }

  private resolveRedirect(
    path: string,
    autoRedirect: boolean,
    context: string,
  ): Observable<string> {
    // Use the centralized base URL resolver so that session/navigation
    // endpoints are routed to the local mock server when mockServerApiEnabled
    // is true, without requiring code changes to switch environments.
    const url = `${this.httpHelperService.resolveBackendBaseUrl()}${path}`;
    return this.http.get<RedirectResponse>(url).pipe(
      map((res) => this.requireRedirectUrl(res, context)),
      tap((redirectUrl) => {
        if (autoRedirect) {
          this.redirect(redirectUrl);
        }
      }),
    );
  }

  private resolveAbsoluteRedirect(
    url: string,
    autoRedirect: boolean,
    context: string,
  ): Observable<string> {
    return this.http.get<RedirectResponse>(url).pipe(
      map((res) => this.requireRedirectUrl(res, context)),
      tap((redirectUrl) => {
        if (autoRedirect) {
          this.redirect(redirectUrl);
        }
      }),
    );
  }

  private requireRedirectUrl(res: RedirectResponse, context: string): string {
    if (!res?.redirectUrl || typeof res.redirectUrl !== "string") {
      throw new Error(
        `[CrossAppNavigationService:${context}] redirectUrl missing from backend response`,
      );
    }
    return res.redirectUrl;
  }

  private normalizeMenuUrl(menuUrl: string): string {
    const baseUrl = this.httpHelperService.resolveBackendBaseUrl();
    if (!/^https?:\/\//i.test(menuUrl)) {
      return `${baseUrl}${menuUrl.startsWith("/") ? "" : "/"}${menuUrl}`;
    }

    return menuUrl
      .replace(new URL(menuUrl).origin, baseUrl)
      .replace("alpha-sat-svc/session/switch/alpha-service", "session");
  }

  private buildAlphaUrl(path: string): string {
    if (!path) {
      return this.alphaBaseUrl;
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.alphaBaseUrl}${normalizedPath}`;
  }

  private buildAlphaSatUrl(path: string): string {
    if (!path) {
      return this.alphaSatBaseUrl;
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.alphaSatBaseUrl}${normalizedPath}`;
  }
}
